/*
 * Daily Drop: countdown engine and page interactions.
 *
 * Spec notes that drove the design here:
 *  - The deadline is server-owned. Liquid renders epoch SECONDS (timezone-proof),
 *    and on load we re-read the server clock from the `Date` response header of a
 *    no-store request. That survives edge caching, which a Liquid-rendered "now"
 *    does not, and it means changing the phone's clock cannot move the timer.
 *  - At zero the section re-renders in place via Shopify's ?section_id= endpoint,
 *    so LIVE -> SOLD/RETURNED and TEASER -> LIVE happen without a page refresh.
 *  - Five visual tiers (>12h, 12-3h, 3-1h, <1h, <5m) so a static timer does not
 *    go invisible after a couple of days.
 *  - Screen readers get milestone announcements only (12h, 3h, 1h, 10m), never a
 *    per-second stream, which would make the page unusable.
 *  - No fabricated urgency anywhere: every number rendered comes from real data.
 */
(function () {
  'use strict';

  /* Offset between the server clock and this device's clock, in ms. */
  var skewMs = 0;

  function serverNow() {
    return Date.now() + skewMs;
  }

  function pushEvent(name, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params || {}));
    } catch (e) {
      /* tracking must never break the page */
    }
  }

  /* ---------------------------------------------------------------- clock */

  /*
   * Seed the offset from the Liquid-rendered server time, then correct it from a
   * live response header. The seed can be stale behind a CDN; the header cannot.
   */
  function syncServerClock(seedEpochSeconds, done) {
    if (seedEpochSeconds) {
      skewMs = seedEpochSeconds * 1000 - Date.now();
    }

    if (typeof window.fetch !== 'function') {
      done();
      return;
    }

    var url = window.location.pathname + '?drop_clock=' + Date.now();
    var settled = false;
    var finish = function () {
      if (settled) return;
      settled = true;
      done();
    };

    /* Never let a hanging request delay the first paint of the timer. */
    setTimeout(finish, 2500);

    window
      .fetch(url, { method: 'HEAD', cache: 'no-store', credentials: 'same-origin' })
      .then(function (response) {
        var header = response.headers.get('Date');
        if (header) {
          var parsed = Date.parse(header);
          if (!isNaN(parsed)) skewMs = parsed - Date.now();
        }
        finish();
      })
      .catch(finish);
  }

  /* ------------------------------------------------------------ formatting */

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function partsFrom(msLeft) {
    var total = Math.max(0, Math.floor(msLeft / 1000));
    return {
      total: total,
      hours: Math.floor(total / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60
    };
  }

  /* Tier thresholds are in seconds, matching the spec table. */
  function tierFor(totalSeconds) {
    if (totalSeconds < 300) return 'final';
    if (totalSeconds < 3600) return 'urgent';
    if (totalSeconds < 10800) return 'soon';
    if (totalSeconds < 43200) return 'warm';
    return 'calm';
  }

  function faceFor(tier, p) {
    if (tier === 'urgent' || tier === 'final') {
      /* Under an hour the hours slot is always zero, so drop it and go big. */
      return [
        { value: pad(p.hours * 60 + p.minutes), label: 'minutes' },
        { value: pad(p.seconds), label: 'seconds' }
      ];
    }
    if (tier === 'soon') {
      return [
        { value: pad(p.hours), label: 'hours' },
        { value: pad(p.minutes), label: 'minutes' },
        { value: pad(p.seconds), label: 'seconds' }
      ];
    }
    return [
      { value: pad(p.hours), label: 'hours' },
      { value: pad(p.minutes), label: 'minutes' }
    ];
  }

  function spokenTime(p) {
    if (p.hours > 0) return p.hours + ' hours and ' + p.minutes + ' minutes remaining';
    if (p.minutes > 0) return p.minutes + ' minutes remaining';
    return 'less than a minute remaining';
  }

  /*
   * "Ends today at 9:30 PM your time". The visitor is international, so the
   * store's own timezone is meaningless to them.
   */
  function localEndSentence(deadlineMs, prefix) {
    var end = new Date(deadlineMs);
    var now = new Date(serverNow());
    var time = end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    var startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    var dayDelta = Math.floor((end.getTime() - startOfToday) / 86400000);

    var when;
    if (dayDelta === 0) when = 'today';
    else if (dayDelta === 1) when = 'tomorrow';
    else when = 'on ' + end.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });

    return prefix + ' ' + when + ' at ' + time + ' your time';
  }

  /* --------------------------------------------------------------- timers */

  var MILESTONES = [
    { at: 43200, said: false, text: '12 hours left in this drop' },
    { at: 10800, said: false, text: '3 hours left in this drop' },
    { at: 3600, said: false, text: '1 hour left in this drop' },
    { at: 600, said: false, text: '10 minutes left in this drop' }
  ];

  function startCountdown(el, onExpire) {
    var deadlineMs = parseInt(el.getAttribute('data-deadline'), 10) * 1000;
    if (!deadlineMs || isNaN(deadlineMs)) return null;

    var face = el.querySelector('[data-drop-face]');
    var live = el.querySelector('[data-drop-live]');
    var localLine = el.querySelector('[data-drop-local]');
    var flag = el.querySelector('[data-drop-finalflag]');
    var isPrimary = el.hasAttribute('data-drop-primary');
    var milestones = isPrimary ? MILESTONES.map(function (m) { return { at: m.at, said: false, text: m.text }; }) : [];

    if (localLine) {
      localLine.textContent = localEndSentence(deadlineMs, localLine.getAttribute('data-prefix') || 'Ends');
    }

    var lastTier = null;
    var expired = false;
    var handle = null;

    function paint() {
      var p = partsFrom(deadlineMs - serverNow());

      if (p.total <= 0 && !expired) {
        expired = true;
        if (handle) clearInterval(handle);
        el.setAttribute('data-tier', 'over');
        if (typeof onExpire === 'function') onExpire();
        return;
      }

      var tier = tierFor(p.total);
      if (tier !== lastTier) {
        el.setAttribute('data-tier', tier);
        if (flag) flag.hidden = tier !== 'final';
        lastTier = tier;
      }

      var segments = faceFor(tier, p);
      /* Rebuild only when the segment count changes, so we are not thrashing DOM every second. */
      if (face) {
        if (face.childElementCount !== segments.length) {
          face.textContent = '';
          segments.forEach(function (seg) {
            var unit = document.createElement('span');
            unit.className = 'dd-timer__unit';
            unit.setAttribute('data-unit', seg.label);
            var num = document.createElement('span');
            num.className = 'dd-timer__num';
            num.textContent = seg.value;
            var lab = document.createElement('span');
            lab.className = 'dd-timer__lab';
            lab.textContent = seg.label;
            unit.appendChild(num);
            unit.appendChild(lab);
            face.appendChild(unit);
          });
        } else {
          var nums = face.querySelectorAll('.dd-timer__num');
          var labs = face.querySelectorAll('.dd-timer__lab');
          for (var i = 0; i < segments.length; i++) {
            if (nums[i] && nums[i].textContent !== segments[i].value) nums[i].textContent = segments[i].value;
            if (labs[i] && labs[i].textContent !== segments[i].label) labs[i].textContent = segments[i].label;
          }
        }
      }

      if (live) {
        for (var m = 0; m < milestones.length; m++) {
          if (!milestones[m].said && p.total <= milestones[m].at) {
            milestones[m].said = true;
            live.textContent = milestones[m].text;
            break;
          }
        }
      }

      el.setAttribute('aria-label', spokenTime(p));
    }

    paint();
    if (!expired) handle = setInterval(paint, 1000);
    return function stop() {
      if (handle) clearInterval(handle);
    };
  }

  /* ------------------------------------------------------------- refresh */

  /*
   * Swap the section for a freshly rendered copy. This is how the state machine
   * advances in an open tab: the server decides the new state, we just re-render.
   */
  function refreshSection(root) {
    if (root.hasAttribute('data-drop-refreshing')) return;
    root.setAttribute('data-drop-refreshing', '');

    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId || typeof window.fetch !== 'function') {
      window.location.reload();
      return;
    }

    var url =
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      '&drop_refresh=' +
      Date.now();

    window
      .fetch(url, { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var next = parsed.querySelector('[data-drop-root]');
        if (!next) {
          window.location.reload();
          return;
        }
        root.parentNode.replaceChild(next, root);
        init(next);
      })
      .catch(function () {
        window.location.reload();
      });
  }

  /* -------------------------------------------------------- interactions */

  function wireInteractions(root) {
    var state = root.getAttribute('data-drop-state') || 'unknown';
    var handle = root.getAttribute('data-drop-handle') || '';

    /* "See it on the wall" */
    var wallToggle = root.querySelector('[data-drop-wall-toggle]');
    var wallPanel = root.querySelector('[data-drop-wall-panel]');
    if (wallToggle && wallPanel) {
      wallToggle.addEventListener('click', function () {
        var open = wallPanel.hasAttribute('hidden');
        if (open) {
          wallPanel.removeAttribute('hidden');
        } else {
          wallPanel.setAttribute('hidden', '');
        }
        wallToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) pushEvent('wall_preview_open', { artwork_handle: handle });
      });
    }

    /* Texture macro shots */
    root.querySelectorAll('[data-drop-zoom]').forEach(function (img) {
      var fire = function () {
        if (img.hasAttribute('data-zoomed')) return;
        img.setAttribute('data-zoomed', '');
        pushEvent('detail_zoom', { artwork_handle: handle });
      };
      img.addEventListener('click', fire);
      img.addEventListener('mouseenter', fire);
    });

    /* Artist video */
    var video = root.querySelector('[data-drop-video]');
    if (video) {
      video.addEventListener('play', function () {
        pushEvent('artist_video_play', { artwork_handle: handle });
      }, { once: true });
    }

    /* "Why half the price?" */
    var reason = root.querySelector('[data-drop-reason]');
    if (reason) {
      reason.addEventListener('toggle', function () {
        if (reason.open) pushEvent('reason_expand', { artwork_handle: handle });
      });
    }

    /* Buy: this form posts straight through to checkout. */
    var buyForm = root.querySelector('[data-drop-buy-form]');
    if (buyForm) {
      buyForm.addEventListener('submit', function () {
        var timer = root.querySelector('[data-drop-primary]');
        var left = null;
        if (timer) {
          var dl = parseInt(timer.getAttribute('data-deadline'), 10) * 1000;
          if (dl) left = Math.max(0, Math.round((dl - serverNow()) / 1000));
        }
        pushEvent('drop_add_to_cart', { artwork_handle: handle, seconds_remaining: left });
        pushEvent('drop_checkout_start', { artwork_handle: handle, seconds_remaining: left });
      });
    }

    /* Notify-me signups, tagged by where on the page they sat. */
    root.querySelectorAll('[data-drop-notify]').forEach(function (form) {
      form.addEventListener('submit', function () {
        pushEvent('notify_signup', {
          form_placement: form.getAttribute('data-drop-notify'),
          drop_state: state
        });
      });
    });

    /* Sticky mobile bar reveals once the main buy button scrolls away. */
    var bar = root.querySelector('[data-drop-sticky]');
    var anchor = root.querySelector('[data-drop-buy-anchor]');
    if (bar && anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          var e = entries[0];
          var above = e.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', !e.isIntersecting && above);
          bar.setAttribute('aria-hidden', !e.isIntersecting && above ? 'false' : 'true');
        },
        { threshold: 0 }
      ).observe(anchor);
    }
  }

  /* ------------------------------------------------------------------ init */

  /*
   * Shopify serves this page from its own full-page cache, and that cache can be
   * tens of minutes old (measured at 43 minutes on production). The state here is
   * rendered server-side from timestamps, so stale HTML shows the wrong state:
   * most damagingly "the next drop is being prepared" while a drop is actually
   * running. Refreshing at countdown zero does not save it, because the stale
   * state often has no countdown at all.
   *
   * The ?section_id= endpoint is NOT page-cached (measured at 0 to 26 seconds
   * old against a 2600-second page). So: compare the timestamp baked into the
   * HTML against the real server clock, and if the render is old, pull a fresh
   * copy of the section straight away. The replacement carries a current
   * timestamp, so this cannot loop.
   */
  var STALE_AFTER_MS = 90 * 1000;

  function isStale(root) {
    var rendered = parseInt(root.getAttribute('data-server-now'), 10);
    if (!rendered || isNaN(rendered)) return false;
    return serverNow() - rendered * 1000 > STALE_AFTER_MS;
  }

  function init(root) {
    if (!root || root.hasAttribute('data-drop-ready')) return;
    root.setAttribute('data-drop-ready', '');

    wireInteractions(root);

    var seed = parseInt(root.getAttribute('data-server-now'), 10);
    syncServerClock(isNaN(seed) ? 0 : seed, function () {
      if (isStale(root)) {
        refreshSection(root);
        return;
      }
      root.querySelectorAll('[data-drop-countdown]').forEach(function (el) {
        startCountdown(el, function () {
          refreshSection(root);
        });
      });

      var primary = root.querySelector('[data-drop-primary]');
      var remaining = null;
      if (primary) {
        var dl = parseInt(primary.getAttribute('data-deadline'), 10) * 1000;
        if (dl) remaining = Math.max(0, Math.round((dl - serverNow()) / 1000));
      }
      /* The homepage banner reports separately, so it cannot inflate drop_view. */
      pushEvent(root.getAttribute('data-drop-view-event') || 'drop_view', {
        drop_state: root.getAttribute('data-drop-state') || 'unknown',
        artwork_handle: root.getAttribute('data-drop-handle') || '',
        seconds_remaining: remaining
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-drop-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor re-renders sections on every setting change. */
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-drop-root]');
    if (root) init(root);
  });
})();
